import React from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";

import { sectionLabelSx } from "src/shell/styles/themeUtils";

import type { Department, Team } from "../../../types/OrganizationTypes";

import { focusRingSx } from "./focusRingSx";

interface TeamDialogProps {
  open: boolean;
  team?: Team | null;
  name: string;
  onNameChange: (value: string) => void;
  accessLevel: string;
  onAccessLevelChange: (value: string) => void;
  deptId: string;
  onDeptIdChange: (value: string) => void;
  departments: Department[];
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onCreate: () => void;
}

const accessLevels = [
  { value: "Admin access", label: "Admin access" },
  { value: "Deploy + read", label: "Deploy + read" },
  { value: "Read only", label: "Read only" },
];

const TeamDialog: React.FC<TeamDialogProps> = ({
  open,
  team,
  name,
  onNameChange,
  accessLevel,
  onAccessLevelChange,
  deptId,
  onDeptIdChange,
  departments,
  saving,
  error,
  onClose,
  onCreate,
}) => {
  const theme = useTheme();
  const isEdit = Boolean(team);
  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose()}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{isEdit ? "Edit Team" : "Create Team"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {error && (
            <Alert severity="error" sx={{ borderRadius: 1 }}>
              {error}
            </Alert>
          )}
          <TextField
            label="Team Name"
            fullWidth
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="e.g. Platform Team"
            disabled={saving}
            sx={focusRingSx(theme)}
          />
          <Box>
            <Typography variant="subtitle2" sx={{ ...sectionLabelSx, mb: 1 }}>
              Access Level
            </Typography>
            <Select
              fullWidth
              value={accessLevel}
              onChange={(e) => onAccessLevelChange(e.target.value)}
              disabled={saving}
              sx={focusRingSx(theme)}
            >
              {accessLevels.map((level) => (
                <MenuItem key={level.value} value={level.value}>
                  {level.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ ...sectionLabelSx, mb: 1 }}>
              Department (optional)
            </Typography>
            <Select
              fullWidth
              value={deptId}
              onChange={(e) => onDeptIdChange(e.target.value)}
              disabled={saving}
              displayEmpty
              sx={focusRingSx(theme)}
              renderValue={(v) => {
                if (!v || v === "") return "No department (unassigned)";
                const dept = departments.find((d) => d.id === Number(v));
                return dept?.name ?? "Unknown";
              }}
            >
              <MenuItem value="">
                <em>No department (unassigned)</em>
              </MenuItem>
              {departments.map((dept) => (
                <MenuItem key={dept.id} value={String(dept.id)}>
                  {dept.name}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{ textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onCreate()}
          disabled={saving || !name.trim()}
          sx={{
            textTransform: "none",
            "&:active": { transform: "scale(0.98)" },
          }}
        >
          {saving ? (
            <CircularProgress size={18} color="inherit" />
          ) : isEdit ? (
            "Save"
          ) : (
            "Create"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TeamDialog;
